import React, { useState } from 'react';
import axios from 'axios';
import { Search, Package, Truck, CheckCircle, XCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import Header from '../components/Header';
import Footer from '../components/Footer';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TrackOrderPage = () => {
  const [orderId, setOrderId] = useState('');
  const [phone, setPhone] = useState('');
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);

  const handleTrackOrder = async (e) => {
    e.preventDefault();
    setLoading(true);
    setSearched(true);

    try {
      const response = await axios.get(`${API_URL}/api/orders/${orderId}`);
      const orderData = response.data;
      
      if (orderData.customer_phone !== phone) {
        toast.error('Phone number does not match order records');
        setOrder(null);
      } else {
        setOrder(orderData);
      }
    } catch (error) {
      console.error('Failed to fetch order:', error);
      toast.error('Order not found. Please check your Order ID.');
      setOrder(null);
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending':
        return <Package className="w-8 h-8 text-yellow-600" />;
      case 'shipped':
        return <Truck className="w-8 h-8 text-blue-600" />;
      case 'delivered':
        return <CheckCircle className="w-8 h-8 text-green-600" />;
      case 'cancelled':
        return <XCircle className="w-8 h-8 text-red-600" />;
      default:
        return <Package className="w-8 h-8 text-zinc-600" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending':
        return 'bg-yellow-100 text-yellow-800 border-yellow-300';
      case 'shipped':
        return 'bg-blue-100 text-blue-800 border-blue-300';
      case 'delivered':
        return 'bg-green-100 text-green-800 border-green-300';
      case 'cancelled':
        return 'bg-red-100 text-red-800 border-red-300';
      default:
        return 'bg-zinc-100 text-zinc-800 border-zinc-300';
    }
  };

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <Header />
      
      <main className="flex-grow">
        <div className="bg-gradient-to-b from-white to-[#F8FAFC] py-16">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h1 className="text-4xl md:text-5xl font-bold text-[#0f172a] mb-4" data-testid="track-order-title">
                Track Your Order
              </h1>
              <p className="text-lg text-zinc-600">
                Enter your Order ID and phone number to check your order status
              </p>
            </div>

            <div className="bg-white rounded-2xl shadow-sm border border-zinc-200 p-8 mb-8">
              <form onSubmit={handleTrackOrder} className="space-y-5" data-testid="track-order-form">
                <div>
                  <Label htmlFor="orderId">Order ID *</Label>
                  <Input
                    id="orderId"
                    data-testid="order-id-input"
                    value={orderId}
                    onChange={(e) => setOrderId(e.target.value)}
                    placeholder="ORD20260213XXXXXX"
                    required
                    className="mt-1"
                  />
                  <p className="text-xs text-zinc-500 mt-1">You can find this in your order confirmation email</p>
                </div>

                <div>
                  <Label htmlFor="phone">Phone Number *</Label>
                  <Input
                    id="phone"
                    type="tel"
                    data-testid="phone-input"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    placeholder="+91 XXXXXXXXXX"
                    required
                    className="mt-1"
                  />
                </div>

                <Button
                  type="submit"
                  data-testid="track-button"
                  disabled={loading}
                  className="w-full bg-[#F97316] hover:bg-[#EA580C] text-white font-bold py-4 rounded-full text-lg"
                >
                  <Search className="w-5 h-5 mr-2" />
                  {loading ? 'Searching...' : 'Track Order'}
                </Button>
              </form>
            </div>

            {searched && !order && !loading && (
              <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
                <XCircle className="w-12 h-12 text-red-600 mx-auto mb-3" />
                <p className="text-lg font-semibold text-red-900">Order Not Found</p>
                <p className="text-sm text-red-700 mt-1">
                  Please check your Order ID and phone number and try again.
                </p>
              </div>
            )}

            {order && (
              <div className="space-y-6" data-testid="order-details">
                <div className="bg-white rounded-2xl shadow-sm border border-zinc-200 p-8">
                  <div className="flex items-center justify-between mb-6">
                    <div>
                      <h2 className="text-2xl font-bold text-[#0f172a]">Order Status</h2>
                      <p className="text-sm text-zinc-500">Order ID: {order.id}</p>
                    </div>
                    <div>{getStatusIcon(order.order_status)}</div>
                  </div>

                  <div className={`border-2 rounded-xl p-4 mb-6 ${getStatusColor(order.order_status)}`}>
                    <p className="text-center text-lg font-bold capitalize">{order.order_status}</p>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <h3 className="font-semibold text-[#0f172a] mb-3">Order Details</h3>
                      <div className="space-y-2 text-sm">
                        <div>
                          <span className="text-zinc-600">Order Date:</span>
                          <span className="ml-2 font-medium text-[#0f172a]">
                            {new Date(order.created_at).toLocaleDateString()}
                          </span>
                        </div>
                        <div>
                          <span className="text-zinc-600">Total Amount:</span>
                          <span className="ml-2 font-medium text-[#064E3B]">
                            ₹{order.total_amount}
                          </span>
                        </div>
                        <div>
                          <span className="text-zinc-600">Payment Method:</span>
                          <span className="ml-2 font-medium text-[#0f172a] uppercase">
                            {order.payment_method}
                          </span>
                        </div>
                      </div>
                    </div>

                    <div>
                      <h3 className="font-semibold text-[#0f172a] mb-3">Delivery Address</h3>
                      <p className="text-sm text-zinc-700">
                        {order.customer_name}<br />
                        {order.address}<br />
                        {order.city}, {order.state}<br />
                        {order.pincode}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-white rounded-2xl shadow-sm border border-zinc-200 p-8">
                  <h3 className="text-xl font-semibold text-[#0f172a] mb-4">Order Items</h3>
                  {order.items.map((item, index) => (
                    <div
                      key={index}
                      className="flex justify-between items-center py-3 border-b border-zinc-200 last:border-0"
                    >
                      <div>
                        <p className="font-medium text-[#0f172a]">{item.product_name}</p>
                        <p className="text-sm text-zinc-500">Quantity: {item.quantity}</p>
                      </div>
                      <p className="font-semibold text-[#064E3B]">₹{item.price}</p>
                    </div>
                  ))}
                </div>

                {order.order_status === 'delivered' && (
                  <div className="bg-green-50 border border-green-200 rounded-xl p-6 text-center">
                    <CheckCircle className="w-12 h-12 text-green-600 mx-auto mb-3" />
                    <p className="text-lg font-semibold text-green-900">Order Delivered Successfully!</p>
                    <p className="text-sm text-green-700 mt-1">
                      Thank you for shopping with us.
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default TrackOrderPage;
